import { FBSDK } from 'us-web-services';

declare global {
  interface Window {
    FB?: FBSDK;
    fbInitialized?: boolean;
  }
}

const SCRIPT_ID = 'facebook-jssdk';
const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const LOAD_TIMEOUT_MS = 10000;
const POLL_INTERVAL_MS = 100;

export class FacebookSdk {
  private static loadPromise: Promise<FBSDK> | null = null;

  static preload(): void {
    FacebookSdk.ensureReady().catch((): void => undefined);
  }

  static ensureReady(): Promise<FBSDK> {
    if (window.FB && window.fbInitialized) {
      return Promise.resolve(window.FB);
    }

    if (!FacebookSdk.loadPromise) {
      const loading = FacebookSdk.load();

      FacebookSdk.loadPromise = loading;
      loading.catch((): void => {
        FacebookSdk.loadPromise = null;
      });
    }

    return FacebookSdk.loadPromise;
  }

  private static async load(): Promise<FBSDK> {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;

    if (!appId) {
      throw new Error('REACT_APP_FACEBOOK_APP_ID is not configured');
    }

    FacebookSdk.injectScript();
    const sdk = await FacebookSdk.waitForSdk();

    if (!window.fbInitialized) {
      sdk.init({
        appId,
        cookie: true,
        xfbml: true,
        version: 'v24.0',
      });
      window.fbInitialized = true;
    }

    return sdk;
  }

  private static injectScript(): void {
    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');

    script.id = SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    document.head.appendChild(script);
  }

  private static waitForSdk(): Promise<FBSDK> {
    return new Promise((resolve, reject): void => {
      const script = document.getElementById(SCRIPT_ID);
      let poll = 0;
      const finish = (sdk: FBSDK | null): void => {
        window.clearInterval(poll);
        if (sdk) {
          resolve(sdk);
        } else {
          script?.remove();
          reject(new Error('Facebook SDK failed to load'));
        }
      };

      script?.addEventListener('error', (): void => finish(null), { once: true });

      const deadline = Date.now() + LOAD_TIMEOUT_MS;

      poll = window.setInterval((): void => {
        if (window.FB) {
          finish(window.FB);
        } else if (Date.now() > deadline) {
          finish(null);
        }
      }, POLL_INTERVAL_MS);
    });
  }
}
